import ApplyV2AttachmentsUploader from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader'
import ApplyV2InputCheckbox from '@core/components/ApplyDialogV2/ApplyV2InputCheckbox'
import ApplyV2InputTextarea from '@core/components/ApplyDialogV2/ApplyV2InputTextarea'
import getAcceptFilesString from '@core/utils/helpers/getAcceptFilesString'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { FormikProvider, useFormik } from 'formik'
import React, { useRef, useCallback, useEffect, useState } from 'react'
import type {
    ApplyV2BaseResponse,
    ApplyV2FileAttachment,
    ApplyV2SettingsSectionName,
    ApplyV2TempBase64File,
    ApplyV2TempCourseFile,
    ApplyV2TempProfileCvData,
    GetApplyV2SettingsResponse,
    ReviewAndApplyFieldAnonymousReason,
    ReviewAndApplyFieldApplicationText,
    ReviewAndApplyFieldAttachments,
    ReviewApplySection,
    V2CourseFile,
} from '@core/app/types/applyV2Type'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import {
    useDeleteApplyV2AttachmentsMutation,
    useGetAllQuestionsV2ApplyQuery,
    usePostApplyV2ApplicationTextMutation,
    usePostApplyV2AttachmentsMutation,
    usePostApplyV2SendApplicationMutation,
    useValidateAllV2ApplyQuery,
} from '@core/app/api/applyJobV2Api'
import CustomRadio from '@core/components/ApplyDialogV2/CustomRadio'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import ApplyV2ReviewApplyStepItem from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep/ApplyV2ReviewApplyStepItem'
import FileUploaderV2 from '@core/components/ApplyDialogV2/FileUploaderV2'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import ApplyV2Textarea from '@core/components/ApplyDialogV2/ApplyV2Textarea'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2Accordion from '@core/components/ApplyDialogV2/ApplyV2Accordion'
import './ApplyV2ReviewApplyStep.scss'
import { Trans } from 'react-i18next'
import * as Yup from 'yup'

const baseClass = 'ApplyV2ReviewApplyStep'

interface Props {
    settingsData: GetApplyV2SettingsResponse | null | undefined
    // navItems: {
    //     name: ApplyV2SettingsSectionName
    //     visible: boolean
    //     required: boolean
    //     order: number
    //     state:
    // }[]
}

function ApplyV2ReviewApplyStep(props: Props) {
    const { getTitle } = useApplyV2NavTranslations()
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
        // isFinalStep,
        // setIsFinalStep,
        isPostApplied,
        onCloseApply,
        setNavItemsStates,
        navItems,
        storageData,
        isValidateLoading,
        isValidateError,
        settingsData,
        postValidateAll,
    } = useApplyV2Context()
    const [radioVal, setRadioVal] = useState<number | string>(1)
    const [isFinal, setIsFinal] = useState(false)
    const [uploadingFilesCount, setUploadingFilesCount] = useState(0)
    const [loadingFilesIds, setLoadingFilesIds] = useState<(string | number)[]>([])
    const debounceRef = useRef<NodeJS.Timeout | null>(null)
    // const {
    //     data: validateData,
    //     isLoading: isValidateLoading,
    //     error: validateError,
    // } = useValidateAllV2ApplyQuery({ uid: postProfileData?.data?.uid })
    const [
        postApplicationTextRequest,
        {
            data: postApplicationTextData,
            isLoading: isPostApplicationTextLoading,
            isSuccess: isPostApplicationTextSuccess,
            isError: isPostApplicationTextError,
            error: postApplicationTextError,
        },
    ] = usePostApplyV2ApplicationTextMutation()
    const [
        postAttachmentRequest,
        { data: postAttachmentData, isLoading: isPostAttachmentLoading, isError: isPostAttachmentError },
    ] = usePostApplyV2AttachmentsMutation()
    const [
        deleteAttachmentRequest,
        { data: deleteAttachmentData, isLoading: isDeleteAttachmentLoading, isError: isDeleteAttachmentError },
    ] = useDeleteApplyV2AttachmentsMutation()
    const [postSendApplicationRequest, { data: postSendData, isLoading: isPostSendLoading, isError: isPostSendError }] =
        usePostApplyV2SendApplicationMutation()

    const {
        data: questionsData,
        isLoading: isQuestionsLoading,
        isFetching: isQuestionsFetching,
        refetch: questionsRefetch,
    } = useGetAllQuestionsV2ApplyQuery({ uid: storageData?.postInitUid || '' }, { skip: !storageData?.postInitUid })

    useEffect(() => {
        console.log('Mount')
        questionsRefetch && questionsRefetch()
    }, [])

    useEffect(() => {
        if (isPostSendLoading) {
            setIsFinal(true)
        }
    }, [isPostSendLoading])

    const isLoading = isPostProfileLoading || isSettingsLoading || isSettingsFetching

    const reviewApply = settingsData?.steps?.reviewApply
    const coverTextLimit = reviewApply?.fields.applicationText.options.maxLength ?? 1500
    const coverTextMin = reviewApply?.fields.applicationText.options.minLength ?? 100
    const coverLetterRequired = reviewApply?.fields.applicationText.required || false
    const attachmentMaxFiles = reviewApply?.fields.attachments.options.maxFiles ?? 1

    const validationSchema = Yup.object().shape({
        applicationText: coverLetterRequired
            ? Yup.string().min(coverTextMin).max(coverTextLimit).required().nullable()
            : Yup.string().min(coverTextMin).max(coverTextLimit).nullable(),
        isAnonymous: Yup.bool(),
        anonymousReason: Yup.string()
            .nullable()
            .when('isAnonymous', {
                is: true,
                then: (schema) => schema.required().min(2),
                otherwise: (schema) => schema.nullable(),
            }),
        attachments: reviewApply?.fields?.attachments.required
            ? Yup.array().max(attachmentMaxFiles).min(1)
            : Yup.array().max(attachmentMaxFiles),
    })

    const formik = useFormik<{
        applicationText: string | null
        anonymousReason: string | null
        attachments: Array<any>
        isAnonymous: boolean
    }>({
        initialValues: {
            applicationText: postProfileData?.data?.applicationText ?? '',
            anonymousReason: postProfileData?.data?.anonymousReason ?? '',
            attachments: postProfileData?.data?.attachments || [],
            isAnonymous: false,
        },
        validationSchema: validationSchema,
        onSubmit: (values, { setSubmitting }) => {
            console.log('onSubmit', values)
            postValidateAll()
                .then((res) => {
                    console.log(res)
                    handlePostSendApplication()
                })
                .catch((errr) => {
                    console.log('error', errr)
                })
                .finally(() => {
                    console.log('Finnaly')
                    setSubmitting(false)
                })
        },
        enableReinitialize: true,
    })

    // useEffect(() => {
    //     if (validateError && 'data' in validateError) {
    //         const dataError = (validateError as FetchBaseQueryError & { data: ApplyV2BaseResponse }).data
    //         const errs = (Array.isArray(dataError?.errors) ? dataError.errors : [])
    //             .map((e: any) => e.data)
    //             .filter(Boolean)
    //             .flat()
    //         setNavItemsStates(
    //             navItems.map((i) => ({
    //                 ...i,
    //                 state: errs.includes(i.name) ? 'error' : 'success',
    //             }))
    //         )
    //     }
    // }, [validateError])

    const handlePostSendApplication = (retry: boolean | undefined = false) => {
        if (!postProfileData?.data?.uid) {
            console.warn('postProfileData is not available')
            return
        }

        if (!retry) {
            setIsFinal(true)
        }

        postSendApplicationRequest({
            uid: postProfileData.data.uid,
        })
            .unwrap()
            .then((response) => {
                //set window.location
            })
            .catch((error) => {
                const errs = (Array.isArray(error?.data?.errors) ? error.data.errors : [])
                    .map((e: any) => e.data)
                    .filter(Boolean)
                    .flat()
                setNavItemsStates(
                    navItems.map((i) => ({
                        ...i,
                        state: errs.includes(i.name) ? 'error' : 'success',
                    }))
                )
                console.error('Error sending application:', errs)
                // setIsFinalStep(false)
            })
    }

    // console.log(navItems)

    const onDeleteAttachment = (
        file: File | ApplyV2TempCourseFile | ApplyV2FileAttachment | ApplyV2TempBase64File | V2CourseFile
    ) => {
        const uid = postProfileData?.data?.uid
        if (!uid) return
        // setFiles((prev) => prev.filter((f) => f.name !== file.name))
        if (!('uid' in file)) {
            console.error('File does not have a UID and cannot be deleted.')
            return
        }
        if ('uid' in file && file.uid) {
            setLoadingFilesIds((prev) => {
                return [...prev, file.uid]
            })
            formik.setFieldValue(
                'attachments',
                formik.values.attachments.filter((fff) => fff.uid !== file.uid)
            )
            deleteAttachmentRequest({
                uid,
                fileUid: file.uid,
            })
                .unwrap()
                .then((response) => {
                    console.log('Attachment deleted successfully:', response)
                })
                .catch((error: any) => {
                    console.error('Error deleting attachment:', error)
                    formik.setFieldValue('attachments', [...formik.values.attachments, file])
                })
                .finally(() => {
                    setLoadingFilesIds((prev) => {
                        return prev.filter((fi) => fi !== file.uid)
                    })
                })
        }
    }

    const handlePostApplicationText = useCallback(
        (val: string) => {
            // setCoverTextError('')
            // setCoverText(val)

            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }

            debounceRef.current = setTimeout(async () => {
                if (!postProfileData?.data?.uid) {
                    console.warn('postProfileData is not available')
                    return
                }
                try {
                    await postApplicationTextRequest({
                        uid: postProfileData?.data?.uid,
                        applicationText: val,
                    }).unwrap()
                } catch (error: any) {
                    const errorMessages = error?.data?.errors || []

                    const errors = errorMessages
                        .filter((err: any) => err.field === 'applicationText')
                        .map((err: any) => err.msg)
                    // setCoverTextError(errors.join(', '))
                    formik.setFieldError('applicationText', errors)
                }
            }, 500)
        },
        [postProfileData?.data?.uid]
    )

    // console.log('ApplyLoading: ', isLoading)
    // console.log('ApplyLoading: ', isValidateLoading)
    // console.log('ApplyLoading: ', formik.isSubmitting)
    // console.log('ApplyLoading: ', isPostSendLoading)
    // console.log('ApplyLoading: ', isValidateError)

    if (isPostSendLoading && isFinal) {
        return (
            <div className={`${baseClass}__final`}>
                <div className={`${baseClass}__final--icon--loader ${baseClass}__final--icon`}>
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M28 0C29.2768 0 30.3119 1.03508 30.3119 2.31193V7.44954C30.3119 8.72638 29.2768 9.76147 28 9.76147C26.7232 9.76147 25.6881 8.72638 25.6881 7.44954V2.31193C25.6881 1.03508 26.7232 0 28 0ZM14 3.75196C15.1057 3.11352 16.5197 3.49237 17.1581 4.59814L19.7269 9.04731C20.3654 10.1531 19.9865 11.567 18.8808 12.2055C17.775 12.8439 16.361 12.4651 15.7226 11.3593L13.1538 6.91012C12.5154 5.80435 12.8942 4.39039 14 3.75196ZM42 3.75196C43.1058 4.39039 43.4846 5.80435 42.8462 6.91012L40.2774 11.3593C39.639 12.4651 38.225 12.8439 37.1192 12.2055C36.0135 11.567 35.6346 10.1531 36.2731 9.04731L38.8419 4.59814C39.4803 3.49237 40.8943 3.11352 42 3.75196ZM3.75196 14C4.39039 12.8942 5.80435 12.5154 6.91012 13.1538L11.3593 15.7226C12.4651 16.361 12.8439 17.775 12.2055 18.8808C11.567 19.9865 10.1531 20.3654 9.04731 19.7269L4.59814 17.1581C3.49237 16.5197 3.11352 15.1057 3.75196 14ZM52.248 14C52.8865 15.1057 52.5076 16.5197 51.4019 17.1581L46.9527 19.7269C45.8469 20.3654 44.433 19.9865 43.7945 18.8808C43.1561 17.775 43.5349 16.361 44.6407 15.7226L49.0899 13.1538C50.1956 12.5154 51.6096 12.8942 52.248 14ZM0 28C0 26.7232 1.03508 25.6881 2.31193 25.6881H7.44954C8.72638 25.6881 9.76147 26.7232 9.76147 28C9.76147 29.2768 8.72638 30.3119 7.44954 30.3119H2.31193C1.03508 30.3119 0 29.2768 0 28ZM46.2385 28C46.2385 26.7232 47.2736 25.6881 48.5505 25.6881H53.6881C54.9649 25.6881 56 26.7232 56 28C56 29.2768 54.9649 30.3119 53.6881 30.3119H48.5505C47.2736 30.3119 46.2385 29.2768 46.2385 28ZM12.2055 37.1192C12.8439 38.225 12.4651 39.639 11.3593 40.2774L6.91012 42.8462C5.80435 43.4846 4.39039 43.1058 3.75196 42C3.11352 40.8943 3.49237 39.4803 4.59814 38.8419L9.04731 36.2731C10.1531 35.6346 11.567 36.0135 12.2055 37.1192ZM43.7945 37.1192C44.433 36.0135 45.8469 35.6346 46.9527 36.2731L51.4019 38.8419C52.5076 39.4803 52.8865 40.8943 52.248 42C51.6096 43.1058 50.1956 43.4846 49.0899 42.8462L44.6407 40.2774C43.5349 39.639 43.1561 38.225 43.7945 37.1192ZM18.8808 43.7945C19.9865 44.433 20.3654 45.8469 19.7269 46.9527L17.1581 51.4019C16.5197 52.5076 15.1057 52.8865 14 52.248C12.8942 51.6096 12.5154 50.1956 13.1538 49.0899L15.7226 44.6407C16.361 43.5349 17.775 43.1561 18.8808 43.7945ZM37.1192 43.7945C38.225 43.1561 39.639 43.5349 40.2774 44.6407L42.8462 49.0899C43.4846 50.1956 43.1058 51.6096 42 52.248C40.8943 52.8865 39.4803 52.5076 38.8419 51.4019L36.2731 46.9527C35.6346 45.8469 36.0135 44.433 37.1192 43.7945ZM28 46.2385C29.2768 46.2385 30.3119 47.2736 30.3119 48.5505V53.6881C30.3119 54.9649 29.2768 56 28 56C26.7232 56 25.6881 54.9649 25.6881 53.6881V48.5505C25.6881 47.2736 26.7232 46.2385 28 46.2385Z"
                            fill="#19191A"
                        />
                    </svg>
                </div>
                <div className={`${baseClass}__final--title`}>
                    {getTitle('10229', 'Processing your application')}...
                </div>
                <div className={`${baseClass}__final--description`}>
                    {getTitle('10230', 'This may take some time')}. {getTitle('10231', 'Please do not close this tab')}.
                </div>
            </div>
        )
    }

    if (isPostSendError && isFinal) {
        return (
            <div className={`${baseClass}__final`}>
                <div className={`${baseClass}__final--icon ${baseClass}__final--icon--error`}>
                    <svg width="51" height="52" viewBox="0 0 51 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M0 25.9997C0 11.9533 11.3869 0.566406 25.4333 0.566406C39.4798 0.566406 50.8667 11.9533 50.8667 25.9997C50.8667 40.0462 39.4798 51.4331 25.4333 51.4331C11.3869 51.4331 0 40.0462 0 25.9997ZM25.4334 30.4331C24.2736 30.4331 23.3334 29.4929 23.3334 28.3331V14.3331C23.3334 13.1733 24.2736 12.2331 25.4334 12.2331C26.5932 12.2331 27.5334 13.1733 27.5334 14.3331V28.3331C27.5334 29.4929 26.5932 30.4331 25.4334 30.4331ZM25.4334 37.6664C26.7221 37.6664 27.7667 36.6217 27.7667 35.3331C27.7667 34.0444 26.7221 32.9997 25.4334 32.9997C24.1447 32.9997 23.1001 34.0444 23.1001 35.3331C23.1001 36.6217 24.1447 37.6664 25.4334 37.6664Z"
                            fill="#B62626"
                        />
                    </svg>
                </div>
                <div className={`${baseClass}__final--title`}>{getTitle('5858', 'Something went wrong')}</div>
                <div className={`${baseClass}__final--description`}>
                    {getTitle('10237', 'We couldn’t submit your application')}. {getTitle('10238', 'Please try again')}.
                </div>
                <div className={`${baseClass}__final--buttons`}>
                    <ApplyV2Button
                        onClick={() => handlePostSendApplication()}
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-retry`}
                    >
                        {getTitle('6106', 'Try again')}
                    </ApplyV2Button>
                    <ApplyV2Button
                        variant={'outline'}
                        onClick={() => setIsFinal(false)}
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-back`}
                    >
                        {getTitle('10235', 'Back to form')}
                    </ApplyV2Button>
                </div>
            </div>
        )
    }

    if (!isPostApplied && isFinal) {
        return (
            <div className={`${baseClass}__final`}>
                <div className={`${baseClass}__final--icon ${baseClass}__final--icon--error`}>
                    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M28 0C12.536 0 0 12.536 0 28C0 43.464 12.536 56 28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0ZM39.8815 23.2408C40.7999 22.3538 40.8252 20.8902 39.9382 19.9718C39.0511 19.0534 37.5875 19.028 36.6691 19.9151L24.9779 31.2077L19.331 25.7533C18.4126 24.8662 16.949 24.8916 16.062 25.81C15.1749 26.7284 15.2003 28.192 16.1186 29.079L23.3717 36.0849C24.2677 36.9503 25.6882 36.9503 26.5841 36.0849L39.8815 23.2408Z"
                            fill="#00853D"
                        />
                    </svg>
                </div>
                <div className={`${baseClass}__final--title`}>{getTitle('10236', 'Applied successfully')}!</div>
                <div className={`${baseClass}__final--description`}>
                    {getTitle(
                        '10240',
                        'Your application is now under review. Best of luck! Feel free to explore your profile or check out other job posts.'
                    )}
                </div>
                <CustomRadio
                    // name={'ApplyReview--considered'}
                    className={`${baseClass}__final--considered`}
                    title={getTitle('10243', 'Do you want to be considered for other job opportunities?')}
                    options={[
                        {
                            label: getTitle('10241', 'Yes, please'),
                            value: 1,
                        },
                        {
                            label: getTitle('10242', 'No, thanks'),
                            value: 0,
                        },
                    ]}
                    selectedValue={radioVal}
                    onChange={(v) => {
                        setRadioVal(v)
                    }}
                />
                <div className={`${baseClass}__final--buttons`}>
                    <ApplyV2Button
                        onClick={() => (window.location.href = '/profile')}
                        leftIcon={
                            <svg
                                width="17"
                                height="16"
                                viewBox="0 0 17 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M5.76657 4.80052C5.76657 3.29094 6.99032 2.06719 8.4999 2.06719C10.0095 2.06719 11.2332 3.29094 11.2332 4.80052C11.2332 6.3101 10.0095 7.53385 8.4999 7.53385C6.99032 7.53385 5.76657 6.3101 5.76657 4.80052ZM8.4999 0.867188C6.32758 0.867188 4.56657 2.6282 4.56657 4.80052C4.56657 6.32717 5.43631 7.65067 6.70733 8.30257C5.63762 8.58915 4.64991 9.13066 3.84529 9.89058C2.6041 11.0628 1.8999 12.6603 1.8999 14.3337C1.8999 14.6651 2.16853 14.9337 2.4999 14.9337C2.83127 14.9337 3.0999 14.6651 3.0999 14.3337C3.0999 13.0014 3.65999 11.7162 4.66924 10.763C5.67965 9.80872 7.05702 9.26706 8.4999 9.26706C9.94279 9.26706 11.3202 9.80872 12.3306 10.763C13.3398 11.7162 13.8999 13.0014 13.8999 14.3337C13.8999 14.6651 14.1685 14.9337 14.4999 14.9337C14.8313 14.9337 15.0999 14.6651 15.0999 14.3337C15.0999 12.6603 14.3957 11.0628 13.1545 9.89058C12.3499 9.13066 11.3622 8.58915 10.2925 8.30257C11.5635 7.65067 12.4332 6.32717 12.4332 4.80052C12.4332 2.6282 10.6722 0.867188 8.4999 0.867188Z"
                                    fill="white"
                                />
                            </svg>
                        }
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-retry`}
                    >
                        {getTitle('6657', 'Go to profile')}
                    </ApplyV2Button>
                    <ApplyV2Button
                        variant={'outline'}
                        onClick={() => (window.location.href = '/profile/applications')}
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-back`}
                        leftIcon={
                            <svg
                                width="17"
                                height="16"
                                viewBox="0 0 17 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M6.2575 2.25669C6.49181 2.02237 6.49181 1.64247 6.2575 1.40816C6.02318 1.17384 5.64329 1.17384 5.40897 1.40816L3.4999 3.31723L2.92417 2.74149C2.68985 2.50718 2.30995 2.50718 2.07564 2.74149C1.84132 2.97581 1.84132 3.3557 2.07564 3.59002L3.07564 4.59002C3.30995 4.82433 3.68985 4.82433 3.92417 4.59002L6.2575 2.25669ZM8.4999 2.39909C8.16853 2.39909 7.8999 2.66772 7.8999 2.99909C7.8999 3.33046 8.16853 3.59909 8.4999 3.59909H14.4999C14.8313 3.59909 15.0999 3.33046 15.0999 2.99909C15.0999 2.66772 14.8313 2.39909 14.4999 2.39909H8.4999ZM6.2575 6.40816C6.49181 6.64247 6.49181 7.02237 6.2575 7.25669L3.92417 9.59002C3.68985 9.82433 3.30995 9.82433 3.07564 9.59002L2.07564 8.59002C1.84132 8.3557 1.84132 7.97581 2.07564 7.74149C2.30995 7.50718 2.68985 7.50718 2.92417 7.74149L3.4999 8.31723L5.40897 6.40816C5.64329 6.17384 6.02318 6.17384 6.2575 6.40816ZM8.4999 7.39909C8.16853 7.39909 7.8999 7.66772 7.8999 7.99909C7.8999 8.33046 8.16853 8.59909 8.4999 8.59909H14.4999C14.8313 8.59909 15.0999 8.33046 15.0999 7.99909C15.0999 7.66772 14.8313 7.39909 14.4999 7.39909H8.4999ZM6.2575 11.4082C6.49181 11.6425 6.49181 12.0224 6.2575 12.2567L3.92417 14.59C3.68985 14.8243 3.30995 14.8243 3.07564 14.59L2.07564 13.59C1.84132 13.3557 1.84132 12.9758 2.07564 12.7415C2.30995 12.5072 2.68985 12.5072 2.92417 12.7415L3.4999 13.3172L5.40897 11.4082C5.64329 11.1738 6.02318 11.1738 6.2575 11.4082ZM8.4999 12.3991C8.16853 12.3991 7.8999 12.6677 7.8999 12.9991C7.8999 13.3305 8.16853 13.5991 8.4999 13.5991H14.4999C14.8313 13.5991 15.0999 13.3305 15.0999 12.9991C15.0999 12.6677 14.8313 12.3991 14.4999 12.3991H8.4999Z"
                                    fill="#19191A"
                                />
                            </svg>
                        }
                    >
                        {getTitle('10244', 'My applications')}
                    </ApplyV2Button>
                    <ApplyV2Button
                        variant={'transparent'}
                        leftIcon={<i className="fa-regular fa-xmark"></i>}
                        onClick={onCloseApply}
                        className={`${baseClass}__final--buttons--btn ${baseClass}__final--buttons--btn-back`}
                    >
                        {getTitle('10245', 'Close application')}
                    </ApplyV2Button>
                </div>
            </div>
        )
    }

    // const sections = navItems.filter((t) => t.name !== 'question')
    const sections = navItems

    return (
        <FormikProvider value={formik}>
            <form className={baseClass} onSubmit={formik.handleSubmit}>
                <ApplySkeleton isLoading={isLoading} width={150} height={24}>
                    <div className="ApplyV2Content__step-name">{getTitle('reviewApply', 'reviewApply')}</div>
                </ApplySkeleton>
                <ApplySkeleton isLoading={isLoading} style={{ minHeight: '100px' }}>
                    <ApplyV2Accordion
                        title={getTitle('10202', 'Review your application')}
                        defaultExpanded={true}
                        className={`${baseClass}__accordion-main`}
                        icon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M3.26689 2.66576C3.26689 2.26075 3.59522 1.93242 4.00023 1.93242H8.06689V5.55464C8.06689 6.62239 8.93248 7.48798 10.0002 7.48798H12.7336V13.3324C12.7336 13.7374 12.4052 14.0658 12.0002 14.0658H4.00023C3.59522 14.0658 3.26689 13.7374 3.26689 13.3324V2.66576ZM9.26689 5.55464V2.253L12.5495 5.96336C12.6319 6.0565 12.6886 6.16835 12.7156 6.28798H10.0002C9.59522 6.28798 9.26689 5.95965 9.26689 5.55464ZM4.00023 0.732422C2.93248 0.732422 2.06689 1.598 2.06689 2.66576V13.3324C2.06689 14.4002 2.93248 15.2658 4.00023 15.2658H12.0002C13.068 15.2658 13.9336 14.4002 13.9336 13.3324V6.88798V6.44928C13.9336 5.97734 13.7609 5.52169 13.4482 5.16823L10.0917 1.37425C9.73059 0.966114 9.21183 0.732422 8.66689 0.732422H4.00023ZM5.06689 9.33242C5.06689 9.00105 5.33552 8.73242 5.66689 8.73242H10.3336C10.6649 8.73242 10.9336 9.00105 10.9336 9.33242C10.9336 9.66379 10.6649 9.93242 10.3336 9.93242H5.66689C5.33552 9.93242 5.06689 9.66379 5.06689 9.33242ZM5.06689 11.6658C5.06689 11.3344 5.33552 11.0658 5.66689 11.0658H10.3336C10.6649 11.0658 10.9336 11.3344 10.9336 11.6658C10.9336 11.9971 10.6649 12.2658 10.3336 12.2658H5.66689C5.33552 12.2658 5.06689 11.9971 5.06689 11.6658Z"
                                    fill="#58585B"
                                />
                            </svg>
                        }
                    >
                        {sections.map((item) => (
                            <ApplyV2ReviewApplyStepItem
                                key={item.name}
                                title={getTitle(item.name)}
                                className={`${baseClass}__accordion-item`}
                                fieldType={item.name}
                                fieldState={item.state}
                                isValidateLoading={isValidateLoading}
                            />
                        ))}
                        {/*<ApplyV2ReviewApplyStepItem className={`${baseClass}__accordion-item`} fieldType={'cv'} />*/}
                        {/*<ApplyV2ReviewApplyStepItem className={`${baseClass}__accordion-item`} fieldType={'profile'} />*/}
                    </ApplyV2Accordion>
                </ApplySkeleton>
                <div className={`${baseClass}__attachs`}>
                    <ApplySkeleton isLoading={isLoading}>
                        <div className={`${baseClass}__attachs__title`}>{getTitle('1111', 'Apply')}</div>
                    </ApplySkeleton>
                    <div className={`${baseClass}__fields`}>
                        {Object.values(reviewApply?.fields ?? [])
                            .filter(
                                (
                                    n:
                                        | ReviewAndApplyFieldAnonymousReason
                                        | ReviewAndApplyFieldApplicationText
                                        | ReviewAndApplyFieldAttachments
                                ) => n.visible
                            )
                            .sort((a, b) => a.order - b.order)
                            .map(
                                (
                                    field:
                                        | ReviewAndApplyFieldAnonymousReason
                                        | ReviewAndApplyFieldApplicationText
                                        | ReviewAndApplyFieldAttachments
                                ) => {
                                    switch (field.name) {
                                        case 'applicationText':
                                            return (
                                                <ApplyV2InputTextarea
                                                    name={field.name}
                                                    isLoading={isPostApplicationTextLoading}
                                                    placeholder={getTitle('7606', 'Description text')}
                                                    value={formik.values[field.name]}
                                                    label={getTitle('10227', 'Cover letter')}
                                                    rightLabel={`${formik.values[field.name]?.length ?? 0}/${coverTextLimit}`}
                                                    errorMessage={formik.errors[field.name]}
                                                    isRequired={field.required}
                                                    onBlur={formik.handleBlur}
                                                    onChange={(e) => {
                                                        formik.handleChange(e)
                                                        handlePostApplicationText(e.target.value)
                                                    }}
                                                />
                                            )
                                        case 'anonymousReason':
                                            return (
                                                <div className={`${baseClass}__anonymousReason`}>
                                                    <ApplyV2InputCheckbox
                                                        className={`${baseClass}__anonymousReason--check`}
                                                        name={'isAnonymous'}
                                                        label={getTitle(
                                                            '8578',
                                                            'Request anonymity in public application lists'
                                                        )}
                                                        errorMessage={formik.errors.isAnonymous}
                                                        onBlur={formik.handleBlur}
                                                        value={formik.values.isAnonymous}
                                                        onChange={(e) => {
                                                            formik.setFieldValue('isAnonymous', e)
                                                            formik.setFieldValue(field.name, null)
                                                        }}
                                                    />
                                                    {formik.values.isAnonymous && (
                                                        <ApplyV2InputTextarea
                                                            className={`${baseClass}__anonymousReason--text`}
                                                            name={field.name}
                                                            isLoading={isPostApplicationTextLoading}
                                                            placeholder={getTitle(
                                                                '6636',
                                                                'Please provide a reason why you want to be anonymous on the government list'
                                                            )}
                                                            value={formik.values[field.name]}
                                                            label={getTitle('2371', 'Reason')}
                                                            errorMessage={formik.errors[field.name]}
                                                            isRequired={true}
                                                            onBlur={formik.handleBlur}
                                                            onChange={(e) => {
                                                                formik.handleChange(e)
                                                                // handlePostApplicationText(e.target.value)
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            )
                                        case 'attachments':
                                            return (
                                                <ApplyV2AttachmentsUploader
                                                    className={`${baseClass}__attachments`}
                                                    name={field.name}
                                                    isShowDelete={true}
                                                    isShowInput={
                                                        formik.values.attachments.length + uploadingFilesCount <
                                                        attachmentMaxFiles
                                                    }
                                                    isRequired={field.required}
                                                    label={getTitle('8166', 'Add attachments')}
                                                    rightLabel={`${formik.values[field.name]?.length ?? 0}/${attachmentMaxFiles}`}
                                                    files={formik.values.attachments}
                                                    onBlur={formik.handleBlur}
                                                    multipleCount={
                                                        attachmentMaxFiles - formik.values.attachments.length
                                                    }
                                                    accept={getAcceptFilesString('ATTACHMENTS')}
                                                    description={field.options?.descriptionText}
                                                    errorMessage={
                                                        formik.errors.attachments && `${formik.errors.attachments}`
                                                    }
                                                    onChange={(attachs) => {
                                                        // console.log(attachs)
                                                        const uid = postProfileData?.data?.uid
                                                        if (!uid) return
                                                        formik.setFieldError('attachments', undefined)
                                                        setUploadingFilesCount(attachs.length)
                                                        const uploadSequentially = async () => {
                                                            for (const attach of attachs) {
                                                                try {
                                                                    const response = await postAttachmentRequest({
                                                                        uid,
                                                                        file: attach,
                                                                    }).unwrap()

                                                                    if (response.data.file) {
                                                                        formik.setFieldValue('attachments', [
                                                                            ...formik.values.attachments,
                                                                            response.data.file,
                                                                        ])
                                                                    }
                                                                } catch (err) {
                                                                    console.log('Upload error:', err)
                                                                } finally {
                                                                    setUploadingFilesCount((prev) => prev - 1)
                                                                }
                                                            }
                                                        }
                                                        uploadSequentially().finally(() => {
                                                            setUploadingFilesCount(0)
                                                        })
                                                    }}
                                                    onDelete={onDeleteAttachment}
                                                    loadingIds={loadingFilesIds}
                                                    uploadingCount={uploadingFilesCount}
                                                />
                                            )
                                    }
                                }
                            )}
                    </div>
                </div>

                <div className={`${baseClass}__footer`}>
                    <ApplySkeleton isLoading={isLoading} style={{ flexGrow: 1 }}>
                        <div className={`${baseClass}__footer--text`}>
                            <Trans
                                i18nKey="10424"
                                defaults="By applying, I confirm that I have read and agree to the <termsLink>Terms of Service</termsLink> and <privacyLink>Privacy Policy</privacyLink>."
                                components={{
                                    termsLink: <a href="/terms-of-service" target="_blank" rel="noreferrer" />,
                                    privacyLink: <a href="/privacy-policy" target="_blank" rel="noreferrer" />,
                                }}
                            />
                        </div>
                    </ApplySkeleton>
                    <ApplySkeleton isLoading={isLoading}>
                        <ApplyV2Button
                            type={'submit'}
                            // type={'button'}
                            className={`${baseClass}__footer--submit`}
                            // onClick={() => handlePostSendApplication()}
                            // isDisabled={!isValidForm || isValidateLoading || isValidateError}
                            // isDisabled={isValidateLoading || isValidateError || !formik.isValid}
                            isLoading={isLoading || isValidateLoading || formik.isSubmitting || isPostSendLoading}
                            onClick={() => {
                                console.log('Values:', formik.values)
                                console.log('Errors:', formik.errors) // Обратитесь к errors через formik
                                console.log('Touched:', formik.touched)
                                console.log('IsValid:', formik.isValid)
                            }}
                        >
                            {getTitle('1111', 'Apply')}
                        </ApplyV2Button>
                    </ApplySkeleton>
                </div>
            </form>
        </FormikProvider>
    )
}

export default ApplyV2ReviewApplyStep
